import { Input, InputGroup } from "@chakra-ui/react";
import { FiPlus, FiSend } from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { IoAnalytics } from "react-icons/io5";
import { SiSimpleanalytics } from "react-icons/si";
import { GiMedicines } from "react-icons/gi";
import { TbActivityHeartbeat } from "react-icons/tb";
import { useState, useRef, useEffect } from "react";
import {
  rxAssistantService,
  type QueryResponse,
  type StreamEvent,
} from "../../services/rxAssistantService";
import { ResultRenderer } from "../../components/rxAssistant/ResultRenderer";
import { toaster } from "../../components/ui/toaster";

export const RXAIAssistant = () => {
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<QueryResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const tabs = [
    {
      label: "Chat",
      path: "/ai-assistant/chat",
    },
    {
      label: "History",
      path: "/ai-assistant/history",
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, streamingMessage]);

  const handleQuickAction = async (prompt: string) => {
    setQuestion(prompt);
    await handleSubmit(prompt);
  };

  const handleSubmit = async (customQuestion?: string) => {
    const queryText = customQuestion || question;
    if (!queryText.trim() || isLoading) return;

    setIsLoading(true);
    setPendingQuestion(queryText);
    setQuestion("");
    setStreamingMessage("Thinking...");

    try {
      await rxAssistantService.sendQueryStream(
        {
          question: queryText,
          session_id: sessionId || undefined,
        },
        (event: StreamEvent) => {
          switch (event.type) {
            case "thinking":
              setStreamingMessage(event.message || "Thinking...");
              break;
            case "session":
              if (event.session_id) {
                setSessionId(event.session_id);
              }
              break;
            case "generating_sql":
              setStreamingMessage(event.message || "Generating SQL...");
              break;
            case "sql_generated":
              setStreamingMessage("Executing query...");
              break;
            case "executing":
              setStreamingMessage(event.message || "Executing...");
              break;
            case "complete":
              setStreamingMessage(null);
              if (event.query_id && event.session_id) {
                const response: QueryResponse = {
                  query_id: event.query_id,
                  session_id: event.session_id,
                  question: event.question || queryText,
                  sql: event.sql || "",
                  visualization: event.visualization || {
                    type: "text",
                    title: "Result",
                    config: {},
                  },
                  results: event.results || [],
                  row_count: event.row_count || 0,
                  execution_time_ms: event.execution_time_ms || 0,
                  success: event.success !== false,
                  created_at: event.created_at || new Date().toISOString(),
                };
                setMessages((prev) => [...prev, response]);

                if (!response.success) {
                  toaster.create({
                    title: "Query failed",
                    description: "Failed to execute query",
                    type: "error",
                    duration: 6000,
                  });
                }
              }
              break;
            case "error":
              setStreamingMessage(null);
              toaster.create({
                title: "Error",
                description: event.message || "Failed to process query",
                type: "error",
                duration: 6000,
              });
              break;
          }
        },
      );
    } catch (error: any) {
      setStreamingMessage(null);
      const errorMessage = error.message || "Failed to send query";
      const isDateRangeError = errorMessage.includes("specify a time period");

      toaster.create({
        title: isDateRangeError ? "Time period required" : "Error",
        description: errorMessage,
        type: isDateRangeError ? "warning" : "error",
        duration: isDateRangeError ? 8000 : 5000,
      });
    } finally {
      setIsLoading(false);
      setPendingQuestion(null);
    }
  };

  return (
    <div className="h-[85dvh] w-full flex flex-col max-w-5xl mx-auto! p-3!">
      {/* Header */}
      <div className="mb-4! flex items-center justify-between">
        <h1 className="text-xl! font-bold!">RX Assistant</h1>
        <nav
          className="flex gap-1 rounded-lg bg-gray-100 p-1! w-fit"
          aria-label="Tabs">
          {tabs.map((tab) => {
            const active = isActive(tab.path);
            return (
              <Link
                key={tab.label}
                to={tab.path}
                className={`
                  px-4! py-2! text-sm! font-medium! rounded-md! transition-all duration-200
                  ${
                    active
                      ? "bg-white text-blue-600! shadow-sm"
                      : "text-gray-600! hover:text-gray-900! hover:bg-gray-200!"
                  }
                `}>
                {tab.label}
              </Link>
            );
          })}
        </nav>
      </div>

      {messages.length === 0 && !isLoading ? (
        /* ─── Empty State / Quick Actions ─── */
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <div className="text-center mb-10">
            <h1 className="text-2xl! font-semibold! text-gray-800!">
              Hi {user?.first_name} 👋
            </h1>
            <p className="text-base! text-gray-500! mt-2!">
              Ask about sales, locations, diseases, drug categories & more
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-4xl">
            <button
              onClick={() =>
                handleQuickAction("Total revenue by state last month")
              }
              className="flex flex-col items-center gap-3 bg-[#E6ECF7] rounded-2xl p-5! cursor-pointer hover:bg-[#D5DBE7] transition-colors">
              <IoAnalytics size={26} color="#0142B1" />
              <p className="text-xs! text-[#0142B1] font-medium text-center leading-snug">
                Revenue by state last month
              </p>
            </button>
            <button
              onClick={() =>
                handleQuickAction("Total revenue by state this month")
              }
              className="flex flex-col items-center gap-3 bg-[#E6ECF7] rounded-2xl p-5! cursor-pointer hover:bg-[#D5DBE7] transition-colors">
              <SiSimpleanalytics size={26} color="#0142B1" />
              <p className="text-xs! text-[#0142B1] font-medium text-center leading-snug">
                Revenue by state this month
              </p>
            </button>
            <button
              onClick={() =>
                handleQuickAction(
                  "Top drug groups by total revenue grouped by state this year",
                )
              }
              className="flex flex-col items-center gap-3 bg-[#E6ECF7] rounded-2xl p-5! cursor-pointer hover:bg-[#D5DBE7] transition-colors">
              <GiMedicines size={26} color="#0142B1" />
              <p className="text-xs! text-[#0142B1] font-medium text-center leading-snug">
                Top Drug Groups by state
              </p>
            </button>
            <button
              onClick={() =>
                handleQuickAction(
                  "Top diseases by revenue grouped by state this year",
                )
              }
              className="flex flex-col items-center gap-3 bg-[#E6ECF7] rounded-2xl p-5! cursor-pointer hover:bg-[#D5DBE7] transition-colors">
              <TbActivityHeartbeat size={26} color="#0142B1" />
              <p className="text-xs! text-[#0142B1] font-medium text-center leading-snug">
                Top Disease by state
              </p>
            </button>
            <button
              onClick={() =>
                handleQuickAction("Top diseases by revenue last month")
              }
              className="flex flex-col items-center gap-3 bg-[#FFF4E6] rounded-2xl p-5! cursor-pointer hover:bg-[#FFE8CC] transition-colors">
              <IoAnalytics size={26} color="#D97706" />
              <p className="text-xs! text-[#D97706] font-medium text-center leading-snug">
                Top Disease last month
              </p>
            </button>
            <button
              onClick={() =>
                handleQuickAction("Top diseases by revenue this month")
              }
              className="flex flex-col items-center gap-3 bg-[#FFF4E6] rounded-2xl p-5! cursor-pointer hover:bg-[#FFE8CC] transition-colors">
              <GiMedicines size={26} color="#D97706" />
              <p className="text-xs! text-[#D97706] font-medium text-center leading-snug">
                Top Disease this month
              </p>
            </button>
            <button
              onClick={() =>
                handleQuickAction(
                  "Compare Antibiotics vs Antimalarials sales this year",
                )
              }
              className="flex flex-col items-center gap-3 bg-[#FFF4E6] rounded-2xl p-5! cursor-pointer hover:bg-[#FFE8CC] transition-colors">
              <SiSimpleanalytics size={26} color="#D97706" />
              <p className="text-xs! text-[#D97706] font-medium text-center leading-snug">
                Compare Groups
              </p>
            </button>
            <button
              onClick={() =>
                handleQuickAction("Analyze sales by LGA in Lagos this month")
              }
              className="flex flex-col items-center gap-3 bg-[#FFF4E6] rounded-2xl p-5! cursor-pointer hover:bg-[#FFE8CC] transition-colors">
              <TbActivityHeartbeat size={26} color="#D97706" />
              <p className="text-xs! text-[#D97706] font-medium text-center leading-snug">
                LGA Analysis
              </p>
            </button>
          </div>
        </div>
      ) : (
        /* ─── Conversation Area ─── */
        <div className="flex-1 overflow-y-auto mb-4 px-6">
          <div className="space-y-10 py-6">
            {messages.map((message, index) => (
              <div key={index} className="space-y-6">
                {/* User Question */}
                <div className="flex justify-end pl-20">
                  <div className="bg-[#E6ECF7] text-[#302E2E] rounded-md rounded-tr-none px-5! py-3! max-w-full md:max-w-2xl">
                    <p className="text-sm! leading-relaxed! font-medium! whitespace-pre-wrap">
                      {message.question}
                    </p>
                  </div>
                </div>

                {/* AI Response */}
                <div className="flex justify-start pr-8">
                  <div className="w-full max-w-2xl space-y-3">
                    {message.success ? (
                      <div className="overflow-hidden">
                        <ResultRenderer
                          visualization={message.visualization}
                          results={message.results}
                          executionTime={message.execution_time_ms}
                        />
                      </div>
                    ) : (
                      <div className="max-w-2xl bg-red-50 border border-red-100 rounded-xl p-4 flex gap-3 items-start">
                        <div className="mt-0.5 shrink-0 text-red-500">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-sm! font-medium! text-red-800!">
                            Query Failed
                          </h4>
                          <p className="text-sm! text-red-600! mt-1!">
                            {message.error_message || "Failed to execute query"}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && pendingQuestion && (
              <div className="space-y-6! mt-2!">
                {/* Pending User Question */}
                <div className="flex justify-end">
                  <div className="bg-[#E6ECF7] text-[#302E2E] rounded-md rounded-tr-none px-5! py-3! max-w-full md:max-w-2xl opacity-70">
                    <p className="text-sm! leading-relaxed! font-medium! whitespace-pre-wrap">
                      {pendingQuestion}
                    </p>
                  </div>
                </div>

                {/* Streaming Status */}
                <div className="flex justify-start">
                  <div className="bg-[#F5F5F5] px-5 py-4 rounded-md rounded-tl-none flex items-center gap-2">
                    <div className="flex space-x-1">
                      <div
                        className="w-2 h-2 bg-[#0142B1] rounded-full animate-bounce"
                        style={{ animationDelay: "0ms" }}></div>
                      <div
                        className="w-2 h-2 bg-[#0142B1] rounded-full animate-bounce"
                        style={{ animationDelay: "150ms" }}></div>
                      <div
                        className="w-2 h-2 bg-[#0142B1] rounded-full animate-bounce"
                        style={{ animationDelay: "300ms" }}></div>
                    </div>
                    <span className="text-sm! font-medium! text-gray-500!">
                      {streamingMessage || "Analyzing..."}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* ─── Input Area ─── */}
      <div className=" space-y-3!">
        <InputGroup
          startElement={<FiPlus />}
          endElement={
            <button
              onClick={() => handleSubmit()}
              disabled={!question.trim() || isLoading}
              className="bg-[#0142B1]! h-6 w-6 flex items-center justify-center rounded-md text-white">
              <FiSend color="#fff" size={12} />
            </button>
          }>
          <Input
            size={"lg"}
            className="w-full"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask about sales, revenue, trends... (e.g., 'revenue this month')"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            disabled={isLoading}
          />
        </InputGroup>
        {/* <div className="flex items-center gap-3 bg-[#FDFDFD] border border-[#DADADA] rounded-2xl px-4 py-[10px] focus-within:border-[#0142B1] focus-within:shadow-sm transition-all">
          <input
            type="text"
            className="flex-1 bg-transparent border-none outline-none text-sm! font-medium! text-[#302E2E]! placeholder:text-[#605F5F]! placeholder:font-medium!"
            placeholder="Ask about sales, revenue, trends... (e.g., 'revenue this month')"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            disabled={isLoading}
          />
          <button
            onClick={() => handleSubmit()}
            disabled={!question.trim() || isLoading}
            className="shrink-0 bg-[#0142B1]! hover:bg-[#013399]! h-10 w-10 flex items-center justify-center rounded-lg text-white! disabled:opacity-40! disabled:cursor-not-allowed! transition-colors">
            <FiSend size={16} />
          </button>
        </div> */}
        <p className="text-xs! text-gray-400! mt-2 text-center">
          RX Assistant can make mistakes. Please verify important information.
        </p>
      </div>
    </div>
  );
};
